import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { u as useEGDSContext, c as classNames, b as EGDSContextWrapper } from '../theme-provider/index-chunk.esm.js';
import { throttle } from 'throttle-debounce';
import { a as EGDSIcon, E as EGDSIconSize } from '../icon/index-chunk.esm.js';
import styles from '../styles/uitk-egds-toolbar.css';
import { a as analyticsTracker } from '../analytics-tracker/index-chunk.esm.js';

const EGDSToolbarNavigationTypeIconsMap = [
    ['close', 'close'],
    ['back', 'arrow_back'],
    ['expand_up', 'expand_less'],
    ['expand_down', 'expand_more'],
];
const EGDSToolbarNavigationTypeIcons = new Map(EGDSToolbarNavigationTypeIconsMap);
const EGDSToolbarPageMarginsMap = [
    ['small', '--spacing__2x'],
    ['large', '--spacing__8x'],
];
const EGDSToolbarPageMargins = new Map(EGDSToolbarPageMarginsMap);

const getClassNameList$4 = classNames.bind(styles);
// To be cleaned up
var ToolbarHeaderType;
(function (ToolbarHeaderType) {
    ToolbarHeaderType["OVERLAY"] = "Overlay";
})(ToolbarHeaderType || (ToolbarHeaderType = {}));
var EGDSToolbarExpandedState;
(function (EGDSToolbarExpandedState) {
    EGDSToolbarExpandedState["EXPANDED"] = "expanded";
    EGDSToolbarExpandedState["COLLAPSED"] = "collapsed";
})(EGDSToolbarExpandedState || (EGDSToolbarExpandedState = {}));
const Toolbar = (props) => {
    const { id, className, contentDomRef, style, toolbarType, children, childClassName, navigationContent, pageMargin, toolbarActionContent, toolbarTitle, toolbarTitleTag, toolbarSubtitle, toolbarTitleA11yText, ...attributes } = props;
    const { data } = useEGDSContext();
    const baseClassName = 'uitk-toolbar';
    const [elevatedToolbar, setElevatedToolbar] = useState(false);
    const [scrollUnderElementBlockSizeStyle, setScrollUnderElementBlockSizeStyle] = useState({});
    const isElevatedToolbarRef = useRef(false);
    const handleScrollRef = useRef(null);
    let expandedState;
    const toggleElevatedState = (isElevated) => {
        setElevatedToolbar(isElevated);
        isElevatedToolbarRef.current = isElevated;
    };
    const handleScrollWithJs = (event) => {
        if (event.target.scrollTop > 0) {
            if (!isElevatedToolbarRef.current) {
                toggleElevatedState(true);
            }
        }
        else {
            toggleElevatedState(false);
        }
    };
    useEffect(() => {
        const domRef = contentDomRef;
        // Use JS to add a classname for scroll, that can
        // be leveraged if CSS scroll animations aren't supported
        // Unfortunately, due to test fails CSS.supports() cannot be used
        // so both JS and non-JS methods must be employed here until full support is reached
        handleScrollRef.current = throttle(100, handleScrollWithJs);
        domRef?.current?.addEventListener('scroll', handleScrollRef.current);
        return () => domRef?.current?.removeEventListener('scroll', handleScrollRef.current);
    }, [contentDomRef, handleScrollRef]);
    // Set a CSS custom property for scroll animations
    useEffect(() => {
        const setScrollUnderElementBlockSizeVal = data?.scrollUnderElement?.current
            ? `${data?.scrollUnderElement?.current?.clientHeight}px`
            : null;
        setScrollUnderElementBlockSizeStyle({ [`--scroll-under-element-blocksize`]: setScrollUnderElementBlockSizeVal });
    }, [data?.scrollUnderElement]);
    const containerClassNames = getClassNameList$4(className, toolbarType && `${baseClassName}-${toolbarType}`, children && `${baseClassName}-has-placeholder`, elevatedToolbar && `${baseClassName}-floating`, contentDomRef && `${baseClassName}-animated`, pageMargin && `${baseClassName}-has-page-margin`, data?.scrollUnderElement && `${baseClassName}-precedes-scroll-under-element`);
    const toolbarActionsClassname = getClassNameList$4(`${baseClassName}-actions`);
    const toolbarSubtitleClassname = getClassNameList$4(`${baseClassName}-subtitle`);
    const Tag = toolbarTitleTag || 'h2';
    const toolbarSubtitleContent = toolbarSubtitle &&
        (typeof toolbarSubtitle === 'string' ? (jsx("p", { className: toolbarSubtitleClassname, "aria-hidden": !!data?.toolbarSubtitle, children: toolbarSubtitle })) : (React.cloneElement(toolbarSubtitle, {
            className: toolbarSubtitleClassname,
            'aria-hidden': !!data?.toolbarSubtitle,
        })));
    const { navType, navIconLabel, navButtonLabel, onClick, onClickAnalytics, navButtonRef, navButtonId } = navigationContent || {};
    const iconType = ((navType && EGDSToolbarNavigationTypeIcons.get(navType)) ||
        EGDSToolbarNavigationTypeIcons.get('close'));
    if (navType === 'expand_down')
        expandedState = EGDSToolbarExpandedState.EXPANDED;
    if (navType === 'expand_up')
        expandedState = EGDSToolbarExpandedState.COLLAPSED;
    const pageMarginStylevalue = (inputval) => {
        const outputValue = {};
        const propBaseName = '--egds-toolbar-page-margin';
        const inputIsString = typeof inputval === 'string';
        if (inputIsString) {
            Object.assign(outputValue, {
                [propBaseName]: `var(${EGDSToolbarPageMargins.get(inputval)})`,
            });
        }
        else {
            Object.entries(inputval).map(([key, value]) => Object.assign(outputValue, {
                [`${propBaseName}-${key}`]: `var(${EGDSToolbarPageMargins.get(value)})`,
            }));
        }
        return outputValue;
    };
    const toolbarStyles = {
        ...style,
        ...(pageMargin && pageMarginStylevalue(pageMargin)),
        ...scrollUnderElementBlockSizeStyle,
    };
    return (jsx(EGDSContextWrapper, { children: () => (jsxs(EGDSToolbarContainer, { id: id, className: containerClassNames, style: toolbarStyles, ...attributes, children: [navigationContent && onClick && (jsxs(EGDSToolbarButton, { onClick: onClick, onClickAnalytics: onClickAnalytics, buttonId: navButtonId, buttonRef: navButtonRef, expandedState: expandedState ?? undefined, containerId: expandedState ? id : undefined, children: [jsx(EGDSIcon, { id: `${navButtonId}-${iconType}-toolbar`, name: iconType, size: EGDSIconSize.MEDIUM, title: navIconLabel }), navButtonLabel] })), jsxs("section", { className: getClassNameList$4(`${baseClassName}-titles`), children: [toolbarTitle && (jsx(Tag, { className: getClassNameList$4(`${baseClassName}-title`), "aria-hidden": toolbarTitleA11yText || data?.toolbarTitle ? 'true' : 'false', children: toolbarTitle })), toolbarTitleA11yText && (jsx(Tag, { className: getClassNameList$4('is-visually-hidden'), children: toolbarTitleA11yText })), toolbarSubtitle && toolbarSubtitleContent] }), toolbarActionContent && jsx("div", { className: toolbarActionsClassname, children: toolbarActionContent }), children && (jsx("section", { className: getClassNameList$4(`${baseClassName}-children`, childClassName), children: children }))] })) }));
};
Toolbar.displayName = 'EGDSToolbar';
Toolbar.defaultProps = {
    pageMargin: 'small',
};

const getClassNameList$3 = classNames.bind(styles);
const EGDSToolbar = (props) => {
    const { elevation, className } = props;
    const wrapperClassNames = getClassNameList$3(elevation && `uitk-toolbar-${elevation}`, className);
    return jsx(Toolbar, { className: wrapperClassNames, ...props });
};
EGDSToolbar.displayName = 'EGDSToolbar';

const getClassNameList$2 = classNames.bind(styles);
const EGDSToolbarContainer = (props) => {
    const { id, className, children, style, tag = 'div', ...attributes } = props;
    // Renders the custom Tag
    const ToolbarHeaderTag = tag || 'div';
    const containerClassName = getClassNameList$2('uitk-toolbar', className);
    return (jsx(ToolbarHeaderTag, { id: id, className: containerClassName, style: style, ...attributes, children: children }));
};
EGDSToolbarContainer.displayName = 'EGDSToolbarContainer';

const getClassNameList$1 = classNames.bind(styles);
const EGDSTransparentToolbar = (props) => {
    const { overlay, className } = props;
    const baseClassName = 'uitk-toolbar-transparent';
    const wrapperClassNames = getClassNameList$1(overlay && `${baseClassName}-with-overlay`, className);
    return jsx(Toolbar, { className: wrapperClassNames, ...props, toolbarType: "transparent" });
};
EGDSTransparentToolbar.displayName = 'EGDSTransparentToolbar';

const getClassNameList = classNames.bind(styles);
const EGDSToolbarButton = (props) => {
    const { disabled, onClick, buttonRef, onClickAnalytics, buttonId, expandedState, containerId, children } = props;
    const analytics = onClickAnalytics && {
        localAnalyticsId: 'Close',
        ...onClickAnalytics,
    };
    let iconContent;
    let buttonLabel;
    // Map through the children and place the ones we want in specific places
    React.Children.forEach(children, (child) => {
        if (!child) {
            return;
        }
        if (child?.type?.displayName?.match(/(EGDS|Uitk)Icon/) && !iconContent) {
            iconContent = child;
        }
        else if (typeof child === 'string' && !buttonLabel) {
            buttonLabel = child;
        }
    });
    const baseClass = 'uitk-toolbar-button';
    const contentClass = `${baseClass}-content`;
    const wrapperClassName = getClassNameList(baseClass, !buttonLabel && `${baseClass}-icon-only`, buttonLabel && `${baseClass}-has-label`);
    const contentClassNames = getClassNameList(contentClass, !buttonLabel && `${contentClass}-icon-only`, buttonLabel && `${contentClass}-has-label`);
    return (jsx("button", { "aria-controls": containerId, "aria-expanded": expandedState ? expandedState === EGDSToolbarExpandedState.EXPANDED : undefined, className: wrapperClassName, "data-testid": "egds-toolbar-button", disabled: disabled, onClick: onClick, onClickCapture: analyticsTracker.bind(undefined, analytics), id: buttonId, ref: buttonRef, type: "button", children: jsxs("span", { className: contentClassNames, children: [iconContent, buttonLabel && jsx("span", { className: getClassNameList(`${contentClass}-label`), children: buttonLabel })] }) }));
};
EGDSToolbarButton.displayName = 'EGDSToolbarButton';

export { EGDSToolbarExpandedState as E, ToolbarHeaderType as T, Toolbar as a, EGDSToolbar as b, EGDSToolbarContainer as c, EGDSTransparentToolbar as d, EGDSToolbarButton as e };
//# sourceMappingURL=index-chunk.esm.js.map
